import { useEffect, useRef, useState } from "react";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { invalidatePageDataCacheEntries } from "./usePageData.js";
import { dispatchPlanningStateChange } from "../utils/planningCrossViewSync.js";
import { tryBeginPlanningMutationActivity } from "../utils/planningMutationActivity.js";
import usePlanningSettingRefresh from "./usePlanningSettingRefresh.js";

// Réglages d'une date du Dispatch (ligne dispatch_days, Jour et Soir).
const MANUAL_PRODUCT_MIXING_POLICY = Object.freeze({
  field: "manual_product_mixing_enabled",
  path: "manual-product-mixing",
  action: "manual_product_mixing",
  defaultEnabled: false,
  failure: "Impossible d’enregistrer le mélange de produits.",
});
export const COMPARTMENT_SHARING_POLICY = Object.freeze({
  field: "compartment_sharing_enabled",
  path: "compartment-sharing",
  action: "compartment_sharing",
  defaultEnabled: false,
  failure: "Impossible d’enregistrer « Compléter un compartiment ».",
});
export const PRODUCT_COMPARTMENT_FILL_POLICY = Object.freeze({
  field: "product_compartment_fill_enabled",
  path: "product-compartment-fill",
  action: "product_compartment_fill",
  defaultEnabled: true,
  failure: "Impossible d’enregistrer les limites de chargement.",
});

export default function useManualProductMixing(results, policy = MANUAL_PRODUCT_MIXING_POLICY) {
  const refresh = usePlanningSettingRefresh(results);
  const [confirmed, setConfirmed] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const dispatchDate = String(results?.dispatch_date || "");
  const dispatchPeriod = results?.ui_dispatch_period || results?.dispatch_period || "day";
  const revision = String(results?.runtime_revision || "");
  const scope = `${dispatchDate}:${dispatchPeriod}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const usesConfirmed = confirmed?.scope === scope
    && (confirmed.revision === revision || confirmed.previousRevisions.includes(revision));
  const enabled = usesConfirmed ? confirmed.enabled : (results?.[policy.field] ?? policy.defaultEnabled) === true;
  const expectedRevision = usesConfirmed ? confirmed.revision : revision;

  useEffect(() => { setError(""); }, [scope]);
  useEffect(() => {
    // A different day, period or independently updated planning owns its state.
    if (confirmed && !usesConfirmed) setConfirmed(null);
  }, [confirmed, usesConfirmed]);

  async function save(nextEnabled, changes = {}) {
    if (inFlight.current || !dispatchDate || !revision) return;
    const finishActivity = tryBeginPlanningMutationActivity();
    if (!finishActivity) {
      setError("Une modification du planning est déjà en cours.");
      return;
    }
    inFlight.current = true;
    setIsSaving(true);
    setError("");
    try {
      const params = new URLSearchParams({ dispatch_period: dispatchPeriod });
      const response = await fetchWithSupabaseAuth(`/api/planning/dispatch/${encodeURIComponent(dispatchDate)}/${policy.path}?${params}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: nextEnabled, expected_runtime_revision: expectedRevision, ...changes }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error || payload?.detail?.error || (typeof payload?.detail === "string" ? payload.detail : policy.failure));
      if (typeof payload[policy.field] !== "boolean" || !payload.runtime_revision) throw new Error("Réponse de sauvegarde invalide.");
      if (currentScope.current === scope) {
        refresh.waitForRefresh(payload.runtime_revision);
        setConfirmed({
          scope,
          // A results request can still return any revision preceding these saves.
          previousRevisions: [...new Set([
            ...(usesConfirmed ? confirmed.previousRevisions : []), revision, expectedRevision,
          ])],
          revision: payload.runtime_revision,
          enabled: payload[policy.field],
          rules: payload.product_compartment_fill_rules,
        });
      }
      return payload;
    } catch (failure) {
      if (currentScope.current === scope) {
        setError(failure instanceof Error ? failure.message : policy.failure);
      }
    } finally {
      invalidatePageDataCacheEntries(["/api/results/latest"]);
      // This endpoint returns a setting, not the updated planning. Reload locally too.
      // Empty period deliberately invalidates both Jour and Soir for this date.
      dispatchPlanningStateChange("dispatch", {
        dispatchDate, dispatchPeriod: "", action: policy.action, revalidateCurrentView: true,
      });
      inFlight.current = false;
      setIsSaving(false);
      finishActivity();
    }
  }
  return { enabled, isSaving: isSaving || refresh.isRefreshing, error: error || refresh.error, save, clearError: () => setError(""), dispatchDate, dispatchPeriod,
    rules: usesConfirmed ? confirmed.rules : results?.product_compartment_fill_rules,
    available: Boolean(dispatchDate && revision) };
}
